import React from 'react';
import classnames from 'classnames';
import { computed } from 'mobx';
import { Location } from 'history';
import { NavLink, RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react';

import { LoginApiService } from 'src/api';
import { ClientDataStore } from '../../stores/client-data.store';
import { HomeStore } from '../../stores/home.store';
import { lazyInject } from 'src/ioc';
import { RouterStore } from '../../stores/router.store';
import styles from './page-header.less';
import { UIStatusStore } from '../../stores/ui-status.store';
import { MembershipNameTypeStore } from '../../stores/membership-name-type.store';
import { NavLinkList } from './nav-link-list';
import { DisplayLogo } from './display-logo';

import { Popover, Button, Stack } from '@servicetitan/design-system';
import '@servicetitan/anvil-fonts/dist/css/anvil-fonts.css';
import '@servicetitan/design-system/dist/system.min.css';

@observer
export class PageHeaderContainer extends React.Component<RouteComponentProps<void>> {
    @lazyInject(ClientDataStore) clientData!: ClientDataStore;
    @lazyInject(RouterStore) router!: RouterStore;
    @lazyInject(LoginApiService) loginApi!: LoginApiService;
    @lazyInject(HomeStore) home!: HomeStore;
    @lazyInject(UIStatusStore) uiStatus!: UIStatusStore;
    @lazyInject(MembershipNameTypeStore) membershipName!: MembershipNameTypeStore;

    @computed get balance() {
        return this.home.homeData ? this.home.homeData.invoiceBalance : -1;
    }
    @computed get jobs() {
        return this.home.homeData ? this.home.homeData.jobCount : -1;
    }
    @computed get showLogout() {
        return this.clientData.isLoggedIn;
    }
    @computed get isAnonymousPayments() {
        return this.clientData.isAnonymousPayments;
    }
    @computed get showRequestAppointment() {
        return (
            (this.clientData.isLoggedIn &&
                this.home.homeData &&
                this.home.homeData.schedulerEnabled) ||
            false
        );
    }
    @computed get showMemberships() {
        return this.clientData.isLoggedIn && this.home.homeData
            ? this.home.homeData.membershipsEnabled
            : false;
    }
    @computed get membershipsTabName() {
        return this.membershipName ? this.membershipName.namePlural : null;
    }
    @computed get membershipsRoute() {
        return this.membershipName ? this.membershipName.membershipsRoute : '/';
    }
    @computed get isSavePaymentMethodsEnabled() {
        return (
            this.clientData.isLoggedIn &&
            this.home.homeData &&
            (this.home.homeData.saveCreditCardEnabled || this.home.homeData.savePayByBankEnabled ||
                this.home.homeData.saveAchEnabled
            )
        );
    }

    handleContactUs = () => {
        this.uiStatus.isContactUsOpen = true;
    };

    render() {
        return <PageHeader container={this} location={this.props.location} />;
    }
}

interface PageHeaderProps {
    container: PageHeaderContainer;
    location: Location;
}

export enum Titles {
    Invoices = 'Invoices',
    History = 'History',
    Memberships = 'Memberships',
    Contracts = 'Contracts',
    Home = 'Home',
    SavedPayment = 'Saved Payment Methods',
}

function pathToTitle(path: string) {
    switch (path) {
        case path.match(/statements/)?.input:
            return Titles.Invoices;
        case path.match(/history/)?.input:
            return Titles.History;
        case path.match(/savedpaymentmethod\/paymentmethod/)?.input:
            return Titles.SavedPayment;
        case path.match(/memberships/)?.input:
            return Titles.Memberships;
        case path.match(/contracts/)?.input:
            return Titles.Contracts;
        default:
            return Titles.Home;
    }
}

export const PageHeader = observer<React.SFC<PageHeaderProps>>(({ container, location }) => {
    const [open, setOpen] = React.useState(false);

    const toggleIpadNav = () => {
        setOpen(!open);
    };

    const closeIpadNav = () => {
        setOpen(false);
    };

    const title = pathToTitle(location.pathname);

    return (
        <div className={classnames('page-header', styles.pageHeader)}>
            <Stack direction="row" spacing="3" alignItems="center" className={styles.headerHeight}>
                <DisplayLogo
                    defaultNavUrl={container.clientData.loginData.defaultUrl}
                    brandData={
                        container.clientData.portalData
                            ? container.clientData.portalData.brandData
                            : undefined
                    }
                />
                {container.clientData.isLoggedIn && (
                    <React.Fragment>
                        <div className={styles.hideIpad}>
                            <nav className={classnames('gray-text', styles.grayText)}>
                                <NavLinkList container={container} />
                            </nav>
                        </div>
                        <div className={styles.showIpad}>
                            <Button
                                fill="subtle"
                                iconPosition="right"
                                onClick={toggleIpadNav}
                                className={classnames('m-r-2', styles.navDropLink)}
                                iconName={open ? 'expand_less' : 'expand_more'}
                            >
                                {title}
                            </Button>
                            <Popover open={open} direction="br" width="auto" padding="s">
                                <Stack
                                    direction="column"
                                    className="p-2"
                                    onClick={closeIpadNav}
                                    spacing="1"
                                >
                                    <NavLinkList container={container} excludePath={title} />
                                </Stack>
                            </Popover>
                        </div>
                    </React.Fragment>
                )}
                <Stack.Item fill />
                {container.showRequestAppointment && (
                    <NavLink to="/schedule">
                        <Button fill="outline" primary>
                            Request an appointment
                        </Button>
                    </NavLink>
                )}
                <div className={classnames('gray-text m-r-2', styles.interaction, styles.grayText)}>
                    {(container.isAnonymousPayments || !container.showLogout) && (
                        <a onClick={container.handleContactUs}>Contact Us</a>
                    )}
                    {container.showLogout && <a onClick={container.clientData.logOut}>Log Out</a>}
                </div>
            </Stack>
        </div>
    );
});
